import { useState } from 'react';
import {
  Search, Plus, MoreVertical, Star, MapPin, Clock,
  ShoppingBag, DollarSign, Eye, Edit2, Power,
  Store, CheckCircle, XCircle,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { mockCafes } from '@/services/mockData';

const statusConfig: Record<string, { bgClass: string; textClass: string; dotClass: string }> = {
  active: { bgClass: 'bg-emerald-100 dark:bg-emerald-900/30', textClass: 'text-emerald-600 dark:text-emerald-400', dotClass: 'bg-emerald-500' },
  suspended: { bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-600 dark:text-red-400', dotClass: 'bg-red-500' },
  pending: { bgClass: 'bg-amber-100 dark:bg-amber-900/30', textClass: 'text-amber-600 dark:text-amber-400', dotClass: 'bg-amber-500' },
};

export default function CafesPage() {
  const { t, formatCurrency, language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedCafe, setSelectedCafe] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const cafes = mockCafes;

  const filteredCafes = cafes.filter((cafe) => {
    const matchSearch = !searchQuery ||
      cafe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cafe.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cafe.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cafe.area.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || cafe.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = [
    { label: language === 'ar' ? 'إجمالي المقاهي' : 'Total Cafes', value: cafes.length, icon: Store, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' },
    { label: language === 'ar' ? 'مقاهي نشطة' : 'Active Cafes', value: cafes.filter(c => c.status === 'active').length, icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
    { label: language === 'ar' ? 'مفتوحة الآن' : 'Open Now', value: cafes.filter(c => c.is_open).length, icon: Clock, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { label: language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue', value: formatCurrency(cafes.reduce((s, c) => s + c.revenue, 0)), icon: DollarSign, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('cafes')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? `إدارة ${cafes.length} مقهى ومطعم` : `Manage ${cafes.length} cafes & restaurants`}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg shadow-purple-500/25"
        >
          <Plus size={18} />
          {language === 'ar' ? 'إضافة مقهى' : 'Add Cafe'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center`}>
                <stat.icon size={22} className={stat.color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">{stat.value}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <Search size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder={language === 'ar' ? 'بحث بالاسم، المالك، المنطقة...' : 'Search by name, owner, area...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full ps-10 pe-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'active', 'suspended'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {status === 'all' ? t('all') : t(status as any)}{' '}
                <span className="text-xs opacity-70">
                  ({status === 'all' ? cafes.length : cafes.filter(c => c.status === status).length})
                </span>
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
            >
              {language === 'ar' ? 'شبكة' : 'Grid'}
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${viewMode === 'table' ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
            >
              {language === 'ar' ? 'جدول' : 'Table'}
            </button>
          </div>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredCafes.map((cafe) => {
            return (
              <div
                key={cafe.id}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg transition-all group"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 dark:from-purple-900/30 dark:to-pink-900/30 p-5 pb-3 relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-white dark:bg-slate-700 rounded-2xl flex items-center justify-center text-2xl shadow-sm">
                        {cafe.logo}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800 dark:text-white">{cafe.name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{cafe.name_en}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {cafe.is_open ? (
                        <span className="px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-full flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                          {language === 'ar' ? 'مفتوح' : 'Open'}
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium rounded-full">
                          {language === 'ar' ? 'مغلق' : 'Closed'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-3">
                  {/* Owner & Contact */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className="font-medium">{language === 'ar' ? 'المالك:' : 'Owner:'}</span>
                    <span>{cafe.owner}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <MapPin size={14} />
                    <span>{cafe.area}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <Clock size={14} />
                    <span>{cafe.opening_time} - {cafe.closing_time}</span>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800 dark:text-white">{cafe.orders_count}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'طلب' : 'Orders'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-800 dark:text-white">{cafe.products_count}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'منتج' : 'Products'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-amber-500 flex items-center justify-center gap-1">
                        <Star size={14} className="fill-amber-500" />{cafe.rating}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t('rating')}</p>
                    </div>
                  </div>

                  {/* Revenue */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'ar' ? 'الإيرادات' : 'Revenue'}
                    </span>
                    <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(cafe.revenue)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={() => setSelectedCafe(cafe)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl text-sm font-medium hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                    >
                      <Eye size={16} />
                      {t('view')}
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                      <Edit2 size={16} />
                      {t('edit')}
                    </button>
                    <button className="p-2 bg-slate-50 dark:bg-slate-700 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50">
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{language === 'ar' ? 'المقهى' : 'Cafe'}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{language === 'ar' ? 'المالك' : 'Owner'}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{language === 'ar' ? 'المنطقة' : 'Area'}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{language === 'ar' ? 'الطلبات' : 'Orders'}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{language === 'ar' ? 'الإيرادات' : 'Revenue'}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{t('rating')}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{t('status')}</th>
                  <th className="text-start px-5 py-3.5 text-sm font-semibold text-slate-600 dark:text-slate-300">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredCafes.map((cafe) => {
                  const sConfig = statusConfig[cafe.status] || statusConfig.active;
                  return (
                    <tr key={cafe.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center text-lg">
                            {cafe.logo}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 dark:text-white">{cafe.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{cafe.name_en}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{cafe.owner}</td>
                      <td className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{cafe.area}</td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-800 dark:text-white">{cafe.orders_count}</td>
                      <td className="px-5 py-4 text-sm font-medium text-emerald-600 dark:text-emerald-400">{formatCurrency(cafe.revenue)}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 text-amber-500">
                          <Star size={14} className="fill-amber-500" />
                          <span className="text-sm font-medium">{cafe.rating}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${sConfig.bgClass} ${sConfig.textClass}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sConfig.dotClass}`} />
                          {t(cafe.status as any)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedCafe(cafe)}
                            className="p-1.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
                          >
                            <Eye size={16} />
                          </button>
                          <button className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredCafes.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Store size={28} className="text-purple-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">{t('noData')}</h3>
          <p className="text-slate-500 dark:text-slate-400">
            {language === 'ar' ? 'لم يتم العثور على مقاهي مطابقة للبحث' : 'No cafes match your search criteria'}
          </p>
        </div>
      )}

      {/* Cafe Detail Modal */}
      {selectedCafe && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedCafe(null)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 dark:from-purple-900/30 dark:to-pink-900/30 p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white dark:bg-slate-700 rounded-2xl flex items-center justify-center text-3xl shadow-sm">
                    {selectedCafe.logo}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">{selectedCafe.name}</h2>
                    <p className="text-slate-500 dark:text-slate-400">{selectedCafe.name_en}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCafe(null)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <XCircle size={24} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'المالك' : 'Owner'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{selectedCafe.owner}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('phone')}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white" dir="ltr">{selectedCafe.phone}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('email')}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{selectedCafe.email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'المنطقة' : 'Area'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{selectedCafe.area}</p>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-1">
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('address')}</p>
                <p className="text-sm font-medium text-slate-800 dark:text-white">{selectedCafe.address}</p>
              </div>

              {/* Working Hours & Delivery */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                  <Clock size={18} className="mx-auto text-blue-500 mb-1" />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'ساعات العمل' : 'Working Hours'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{selectedCafe.opening_time} - {selectedCafe.closing_time}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                  <DollarSign size={18} className="mx-auto text-emerald-500 mb-1" />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{formatCurrency(selectedCafe.delivery_fee)}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                  <ShoppingBag size={18} className="mx-auto text-purple-500 mb-1" />
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'أقل طلب' : 'Min Order'}</p>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{formatCurrency(selectedCafe.min_order)}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{selectedCafe.orders_count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'طلب' : 'Orders'}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(selectedCafe.revenue)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'الإيرادات' : 'Revenue'}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{selectedCafe.products_count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'منتج' : 'Products'}</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-amber-500 flex items-center justify-center gap-1">
                    <Star size={16} className="fill-amber-500" />{selectedCafe.rating}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('rating')}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                {selectedCafe.status === 'active' ? (
                  <button className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                    <Power size={18} />
                    {language === 'ar' ? 'إيقاف' : 'Suspend'}
                  </button>
                ) : (
                  <button className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors">
                    <CheckCircle size={18} />
                    {language === 'ar' ? 'تفعيل' : 'Activate'}
                  </button>
                )}
                <button className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                  <Edit2 size={18} />
                  {t('edit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Cafe Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                {language === 'ar' ? 'إضافة مقهى جديد' : 'Add New Cafe'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: language === 'ar' ? 'اسم المقهى (عربي)' : 'Cafe Name (Arabic)', placeholder: 'مقهى...' },
                { label: language === 'ar' ? 'اسم المقهى (إنجليزي)' : 'Cafe Name (English)', placeholder: 'Cafe...' },
                { label: language === 'ar' ? 'اسم المالك' : 'Owner Name', placeholder: language === 'ar' ? 'الاسم...' : 'Name...' },
                { label: t('phone'), placeholder: '01xxxxxxxxx' },
                { label: t('email'), placeholder: 'cafe@example.com' },
                { label: t('address'), placeholder: language === 'ar' ? 'العنوان الكامل...' : 'Full address...' },
              ].map((field, i) => (
                <div key={i}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{field.label}</label>
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                  />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {language === 'ar' ? 'وقت الافتتاح' : 'Opening Time'}
                  </label>
                  <input
                    type="time"
                    defaultValue="08:00"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {language === 'ar' ? 'وقت الإغلاق' : 'Closing Time'}
                  </label>
                  <input
                    type="time"
                    defaultValue="22:00"
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors font-medium"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => { setShowAddModal(false); }}
                  className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl hover:from-purple-700 hover:to-purple-600 transition-all font-medium"
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
